/**
 * Session status guard.
 *
 * Complements the push path (session:closed over WebSocket) with a pull check at
 * wake-up points, so a tab restored after hours does not hang on a stale screen:
 * - session gone (404) or CLOSED  -> pending redirect to home (watcher navigates + clears)
 * - session back in LOBBY while on /game/*  -> navigate to the lobby route
 * - network/other errors          -> ignored (a flaky backend must not log anyone out)
 */

import { useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { getSession } from '@/services/api'
import type { ApiError } from '@/services/api'
import { useGameStore } from '@/stores/gameStore'
import { usePlayerStore } from '@/stores/playerStore'
import { useRedirectWatcher } from '@/hooks/useRedirectWatcher'

export function useSessionGuard() {
  useRedirectWatcher()

  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ code?: string }>()
  const storedCode = usePlayerStore((s) => s.sessionCode)
  const sessionUuid = usePlayerStore((s) => s.sessionUuid)
  const role = usePlayerStore((s) => s.role)

  const isGameRoute = location.pathname.startsWith('/game/')
  // Lobby routes carry the session code, so they are checked against the session the
  // screen actually shows. Game routes carry a UUID, and getSession() expects a code,
  // so there the player store is the only source.
  const sessionCode = params.code ?? storedCode

  useEffect(() => {
    // Both flags are scoped to this effect run: `cancelled` stops a late response from
    // navigating after the route/session changed, `inFlight` keeps the check
    // single-flight without leaking state across runs.
    let cancelled = false
    let inFlight = false

    const checkStatus = async () => {
      if (!sessionCode || inFlight) return
      inFlight = true

      try {
        const session = await getSession(sessionCode)
        if (cancelled) return

        if (session.status === 'closed') {
          useGameStore.getState().setPendingRedirect(0, 'home', sessionUuid)
          return
        }

        if (session.status === 'lobby' && isGameRoute) {
          const lobbyPath = role === 'tv'
            ? `/session/${sessionCode}/lobby?mode=tv`
            : `/session/${sessionCode}/lobby`
          // replace: the abandoned game route must not stay in history, or "back"
          // would return to it and be bounced here again
          navigate(lobbyPath, { replace: true })
        }
      } catch (error) {
        if (cancelled) return
        // Only a definitive 404 means the session is gone; anything else
        // (network failure, 5xx) is transient and must not clear the session.
        if ((error as ApiError)?.status === 404) {
          useGameStore.getState().setPendingRedirect(0, 'home', sessionUuid)
        }
      } finally {
        inFlight = false
      }
    }

    void checkStatus()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkStatus()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [sessionCode, sessionUuid, isGameRoute, role, navigate])
}
